// Auth service for StockerAI v3 - compatible with backend JWT (same as v2)
import { fetchApi, ApiError } from './api';

export interface User {
  id: number;
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
  name?: string;
  is_superuser?: boolean;
  is_staff?: boolean;
  role?: {
    id: number;
    name: string;
    description?: string;
  };
  organization?: {
    id: number;
    name: string;
    code?: string;
    currency?: string;
  };
}

export interface LoginResponse {
  access: string;
  refresh: string;
  user: User;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export const authService = {
  login: async (credentials: LoginRequest): Promise<User> => {
    try {
      const response = await fetchApi<LoginResponse>('auth/token/', {
        method: 'POST',
        body: credentials,
        withAuth: false,
      });

      localStorage.setItem('auth_token', response.access);
      localStorage.setItem('refresh_token', response.refresh);

      const user: User = {
        ...response.user,
        name:
          response.user.name ||
          `${response.user.first_name || ''} ${response.user.last_name || ''}`.trim() ||
          response.user.username,
      };

      localStorage.setItem('user', JSON.stringify(user));
      return user;
    } catch (error) {
      const msg =
        error && typeof error === 'object' && 'detail' in error
          ? String((error as ApiError).detail || (error as ApiError).message)
          : error instanceof Error
            ? error.message
            : 'Error de autenticación';
      throw new Error(msg);
    }
  },

  logout: () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  },

  getCurrentUser: (): User | null => {
    const userJson = localStorage.getItem('user');
    return userJson ? (JSON.parse(userJson) as User) : null;
  },

  isAuthenticated: (): boolean => {
    return !!localStorage.getItem('auth_token');
  },

  refreshToken: async (): Promise<boolean> => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return false;
    try {
      const response = await fetchApi<{ access: string }>('auth/token/refresh/', {
        method: 'POST',
        body: { refresh: refreshToken },
        withAuth: false,
      });
      localStorage.setItem('auth_token', response.access);
      return true;
    } catch {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      return false;
    }
  },
};
